import { useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { ChevronRight, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { hasPermission, useClaims } from "@/hooks/use-claims";
import type { TFunction } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { ApiRequestError } from "@/lib/api-error";
import { formatCurrency, formatNumber, parseLocaleNumber } from "@/lib/format";
import { itemDisplayName } from "@/lib/inventory";
import { useAdminOrgStore } from "@/stores/admin-org";

import { CATEGORY_OF_COURSE, COURSES, COURSE_LABELS, courseOf, type Course } from "./course";
import { BackLink, LoadError, PickASite, RowsSkeleton, useGoBack, useOpen } from "./menu-parts";
import {
  CADENCE_LABELS,
  datesLabel,
  hasEnded,
  isReading,
  menuLabel,
  menuRank,
  runsAt,
  serviceTime,
  type Menu,
} from "./menu-model";
import { useSiteCurrency, useSiteMenus, useSiteToday } from "./use-menus";
import {
  useDishMenus,
  useInventorySearch,
  useRecipe,
  useRecipeActions,
  type InventoryItem,
  type MethodStep,
  type NewLine,
  type Recipe,
  type RecipeLine,
  type Unit,
} from "./use-recipe";

/**
 * The recipe page (POS2-1456): one dish at the site — its cost from the ingredient lines, its
 * method, and the menus it is on with their planned covers. Edit changes the course, the
 * quantities, the method and the covers, and a save reaches every menu the dish is on.
 * `/menus/recipes/new` adds a dish by hand to the menus picked.
 */

type ShownUnit = "g" | "ml" | "each";
const SHOWN_UNITS: ShownUnit[] = ["g", "ml", "each"];
/** A line added on the page is stored per yield in kg, L or each, as extracted lines are. */
const STORED_AS: Record<ShownUnit, Unit> = { g: "kg", ml: "L", each: "each" };

/** Per portion, kg and L read as g and ml. */
const SHOWN_AS: Record<Unit, ShownUnit> = {
  g: "g",
  kg: "g",
  ml: "ml",
  L: "ml",
  each: "each",
};
const SHOWN_PER_STORED: Record<Unit, number> = { g: 1, kg: 1000, ml: 1, L: 1000, each: 1 };
const AMOUNT_KEYS = {
  g: "menus.recipe.grams",
  ml: "menus.recipe.millilitres",
  each: "menus.recipe.each",
} as const;
const UNIT_KEYS = {
  g: "menus.recipe.unitG",
  ml: "menus.recipe.unitMl",
  each: "menus.recipe.unitEach",
} as const;
const REF = /\{(\d+)\}/g;

/** A step being edited; `origin` is the stored step it started from, if any. */
type DraftStep = { origin: MethodStep | null; text: string };

/** A line added on the page: a catalogue item and its amount per portion as typed. */
interface AddedLine {
  key: number;
  item: InventoryItem;
  amount: string;
  unit: ShownUnit;
}

interface AddedFigure {
  line: AddedLine;
  amount: number | null;
  cost: number | null;
  weightKg: number | null;
}

interface Draft {
  course: Course | null;
  /** Line id → amount per portion as typed, in the unit it is shown in. */
  amounts: Record<string, string>;
  /** Ids of the lines taken off the recipe. */
  removed: string[];
  added: AddedLine[];
  steps: DraftStep[];
  /** Menu id → planned covers as typed; empty for none. */
  covers: Record<string, string>;
}

export default function RecipePage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const siteId = useAdminOrgStore((s) => s.siteId);
  const siteName = useAdminOrgStore((s) => s.siteName);
  if (!siteId) {
    return (
      <div className="mx-auto max-w-5xl">
        <BackLink label={t("menus.recipes.title")} to="/menus/recipes" />
        <PickASite />
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-5xl">
      {id ? (
        <ExistingRecipe
          key={`${id}:${siteId}`}
          recipeId={id}
          siteId={siteId}
          siteName={siteName}
          fromMenu={params.get("from")}
        />
      ) : (
        <NewRecipe key={siteId} siteId={siteId} menuId={params.get("menu")} />
      )}
    </div>
  );
}

function ExistingRecipe({
  recipeId,
  siteId,
  siteName,
  fromMenu,
}: {
  recipeId: string;
  siteId: string;
  siteName: string;
  /** The menu the page was opened from, for the back link. */
  fromMenu: string | null;
}) {
  const { t, locale } = useTranslation();
  const openPage = useOpen();
  const { isPlatformAdmin, permissions } = useClaims();
  const canEdit = isPlatformAdmin || hasPermission(permissions, "recipe:update");
  const recipeQuery = useRecipe(recipeId);
  const dishQuery = useDishMenus(recipeId, siteId);
  const menusQuery = useSiteMenus(siteId, false);
  const currency = useSiteCurrency(siteId);
  const today = useSiteToday(siteId);
  const { save } = useRecipeActions();
  // Null while viewing.
  const [draft, setDraft] = useState<Draft | null>(null);
  const [matching, setMatching] = useState<RecipeLine | null>(null);
  const [adding, setAdding] = useState(false);

  if (recipeQuery.isPending || dishQuery.isPending || menusQuery.isPending || today === null) {
    return <RecipeSkeleton />;
  }
  if (recipeQuery.isError) {
    if (recipeQuery.error instanceof ApiRequestError && recipeQuery.error.status === 404) {
      return <p className="py-14 text-center text-sm text-gray-500">{t("menus.recipeNotFound")}</p>;
    }
    return <LoadError onRetry={() => recipeQuery.refetch()} />;
  }
  if (dishQuery.isError) return <LoadError onRetry={() => dishQuery.refetch()} />;
  if (menusQuery.isError) return <LoadError onRetry={() => menusQuery.refetch()} />;

  const recipe = recipeQuery.data;
  const portions = recipe.yield_portions;
  const label = (m: Menu) => menuLabel(t, locale, m);
  const byId = new Map(menusQuery.data.map((m) => [m.id, m]));
  // The site's list is a separate read: a menu archived in between is simply not shown.
  const onMenus = dishQuery.data
    .flatMap((d) => {
      const menu = byId.get(d.menu_id);
      return menu ? [{ menu, covers: d.planned_covers }] : [];
    })
    .sort(
      (a, b) =>
        menuRank(a.menu, siteId, today) - menuRank(b.menu, siteId, today) ||
        label(a.menu).localeCompare(label(b.menu)),
    );
  const names = recipe.ingredients.map((line, i) => lineName(t, line, i));
  const steps = storedSteps(recipe);
  const course = courseOf(recipe.category_slug);
  const amountText = (line: RecipeLine) => amountInput(locale, perPortion(line, portions));
  const coversText = (covers: number | null) => (covers === null ? "" : String(covers));
  const money = (amount: number | null) =>
    amount === null ? "-" : formatCurrency(locale, amount, currency, 2);
  const openMenu = (menuId: string) => openPage(`/menus/${menuId}`, recipe.name);

  const kept = draft
    ? recipe.ingredients.filter((line) => !draft.removed.includes(line.id))
    : recipe.ingredients;
  // Costs and weights per portion, recalculated as amounts are typed.
  const figures = kept.map((line) => {
    const original = perPortion(line, portions);
    const typed = draft ? parseLocaleNumber(locale, draft.amounts[line.id]) : original;
    const amount = typed !== null && typed > 0 ? typed : null;
    const ratio = (amount ?? original) / original;
    const cost = line.cost?.amount ?? null;
    return {
      line,
      name: names[recipe.ingredients.indexOf(line)],
      amount,
      cost: cost === null ? null : (cost * ratio) / portions,
      weightKg: line.weight_kg === null ? null : (line.weight_kg * ratio) / portions,
    };
  });
  const added = (draft?.added ?? []).map((line) => addedFigure(locale, line));
  const all = [...figures, ...added];
  const priced = all.flatMap((f) => (f.cost === null ? [] : [f.cost]));
  const costPerPortion = priced.length > 0 ? priced.reduce((a, b) => a + b, 0) : null;
  const weights = all.flatMap((f) => (f.weightKg === null ? [] : [f.weightKg]));
  const weightKg =
    weights.length > 0 && weights.length === all.length ? weights.reduce((a, b) => a + b, 0) : null;
  const perKg = costPerPortion !== null && weightKg ? costPerPortion / weightKg : null;
  const unmatched = recipe.ingredients.filter((line) => !line.inventory_item_id).length;
  const valid =
    all.every((f) => f.amount !== null) &&
    Object.values(draft?.covers ?? {}).every((v) => /^\d*$/.test(v.trim()));

  function startEditing() {
    setDraft({
      course,
      amounts: Object.fromEntries(recipe.ingredients.map((l) => [l.id, amountText(l)])),
      removed: [],
      added: [],
      steps: steps.map((s) => ({ origin: s, text: render(s.text, names) })),
      covers: Object.fromEntries(onMenus.map((m) => [m.menu.id, coversText(m.covers)])),
    });
  }

  function submit(d: Draft) {
    const coursePicked = d.course === course;
    save.mutate(
      {
        recipeId,
        body: {
          site_id: siteId,
          category_slug:
            coursePicked || d.course === null ? recipe.category_slug : CATEGORY_OF_COURSE[d.course],
          method_steps: savedSteps(
            d.steps,
            names,
            recipe.ingredients.flatMap((line, i) => (d.removed.includes(line.id) ? [] : [i])),
          ),
          lines: kept.map((line) => {
            const typed = d.amounts[line.id];
            const amount = parseLocaleNumber(locale, typed)!;
            return {
              id: line.id,
              inventory_item_id: line.inventory_item_id,
              quantity:
                typed === amountText(line)
                  ? line.quantity
                  : (amount * portions) / SHOWN_PER_STORED[line.unit_of_measure],
            };
          }),
          added: added.map((f) => newLine(f.line, f.amount!, portions)),
          covers: onMenus
            .filter((m) => d.covers[m.menu.id] !== coversText(m.covers))
            .map((m) => {
              const typed = d.covers[m.menu.id].trim();
              return { menu_id: m.menu.id, planned_covers: typed === "" ? null : Number(typed) };
            }),
        },
      },
      {
        onSuccess: () => {
          setDraft(null);
          toast.success(t("menus.recipe.saved", { name: recipe.name }));
        },
      },
    );
  }

  /** A catalogue match is a save of the stored recipe with one line's item set. */
  function match(line: RecipeLine, item: InventoryItem) {
    save.mutate(
      {
        recipeId,
        body: {
          site_id: siteId,
          category_slug: recipe.category_slug,
          method_steps: steps,
          lines: recipe.ingredients.map((l) => ({
            id: l.id,
            quantity: l.quantity,
            inventory_item_id: l.id === line.id ? item.id : l.inventory_item_id,
          })),
          added: [],
          covers: [],
        },
      },
      {
        onSuccess: () => {
          setMatching(null);
          toast.success(
            t("menus.recipe.matched", {
              name: lineName(t, line, recipe.ingredients.indexOf(line)),
              item: itemDisplayName(item),
            }),
          );
        },
      },
    );
  }

  const stats = [
    { label: t("menus.recipe.statCost"), value: money(costPerPortion) },
    { label: t("menus.recipe.statPerKg"), value: money(perKg) },
    {
      label: t("menus.recipe.statWeight"),
      value:
        weightKg === null
          ? "-"
          : t("menus.recipe.grams", {
              value: formatNumber(locale, weightKg * 1000, { maximumFractionDigits: 0 }),
            }),
    },
    { label: t("menus.ingredients"), value: String(all.length) },
  ];
  const th = "text-xs uppercase tracking-wide text-gray-400";

  return (
    <>
      <RecipeBack menuId={fromMenu} menus={menusQuery.data} />
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-medium text-gray-900">{recipe.name}</h1>
          {draft ? (
            <CoursePills value={draft.course} onChange={(c) => setDraft({ ...draft, course: c })} />
          ) : (
            <p className="text-sm text-gray-500">
              {[
                course ? t(COURSE_LABELS[course]) : null,
                t("menus.recipe.onMenus", { count: onMenus.length }),
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </div>
        {canEdit &&
          (draft ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setDraft(null)}>
                {t("common.cancel")}
              </Button>
              <Button size="sm" disabled={!valid || save.isPending} onClick={() => submit(draft)}>
                {save.isPending ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={startEditing}>
              {t("menus.recipe.edit")}
            </Button>
          ))}
      </div>
      {draft && onMenus.length > 1 && (
        <p className="mb-4 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500">
          {t("menus.recipe.appliesToAll", { count: onMenus.length })}
        </p>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 border-b border-black/[0.06] pb-5 sm:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="text-xs text-gray-400">{s.label}</p>
            <p className="text-sm font-medium tabular-nums text-gray-900">{s.value}</p>
          </div>
        ))}
      </div>

      {unmatched > 0 && (
        <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t("menus.recipe.unmatched", { count: unmatched })}
        </p>
      )}

      <h2 className="mb-2 text-sm font-semibold text-gray-900">{t("menus.ingredients")}</h2>
      {all.length === 0 && !draft ? (
        <p className="mb-8 text-sm text-gray-500">{t("menus.noIngredientsAdded")}</p>
      ) : (
        <div
          className={`${draft ? "mb-2" : "mb-8"} overflow-hidden rounded-lg border border-black/[0.06] bg-white`}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={`pl-4 ${th}`}>{t("menus.recipe.columnIngredient")}</TableHead>
                <TableHead className={`w-28 text-right ${th}`}>
                  {t("menus.costPerPortion")}
                </TableHead>
                <TableHead className={`hidden w-24 text-right sm:table-cell ${th}`}>
                  {t("menus.recipe.columnPricePerKg")}
                </TableHead>
                <TableHead className={`w-24 pr-4 text-right ${th}`}>
                  {t("menus.columnCost")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {figures.map(({ line, name, amount, cost }) => {
                const unit = SHOWN_AS[line.unit_of_measure];
                return (
                  <TableRow key={line.id}>
                    <TableCell className="whitespace-normal py-2.5 pl-4">
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900">{name}</p>
                          {line.inventory_item_id ? (
                            <p className="text-xs text-gray-400">{t("menus.detail.matched")}</p>
                          ) : (
                            <p className="flex flex-wrap items-center gap-x-2 text-xs text-amber-700">
                              {t("menus.recipe.noMatch")}
                              {canEdit && !draft && (
                                <button
                                  type="button"
                                  className="font-medium underline"
                                  onClick={() => setMatching(line)}
                                >
                                  {t("menus.recipe.match")}
                                </button>
                              )}
                            </p>
                          )}
                        </div>
                        {draft && (
                          <RemoveButton
                            name={name}
                            onClick={() =>
                              setDraft({ ...draft, removed: [...draft.removed, line.id] })
                            }
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {draft ? (
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            value={draft.amounts[line.id]}
                            inputMode="decimal"
                            aria-label={t("menus.recipe.amountLabel", { name })}
                            aria-invalid={amount === null}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                amounts: { ...draft.amounts, [line.id]: e.target.value },
                              })
                            }
                            className="h-8 w-16 text-right tabular-nums"
                          />
                          <span className="text-xs text-gray-400">{t(UNIT_KEYS[unit])}</span>
                        </div>
                      ) : (
                        <span className="text-sm tabular-nums text-gray-900">
                          {t(AMOUNT_KEYS[unit], {
                            value: formatNumber(locale, perPortion(line, portions), {
                              maximumFractionDigits: 1,
                            }),
                          })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden text-right text-sm tabular-nums text-gray-500 sm:table-cell">
                      {money(line.item_price_per_kg)}
                    </TableCell>
                    <TableCell className="pr-4 text-right text-sm tabular-nums text-gray-900">
                      {money(cost)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {draft &&
                added.map((figure) => (
                  <AddedRow
                    key={figure.line.key}
                    figure={figure}
                    money={money}
                    onChange={(line) =>
                      setDraft({
                        ...draft,
                        added: draft.added.map((l) => (l.key === line.key ? line : l)),
                      })
                    }
                    onRemove={() =>
                      setDraft({
                        ...draft,
                        added: draft.added.filter((l) => l.key !== figure.line.key),
                      })
                    }
                  />
                ))}
              <TableRow className="hover:bg-transparent">
                <TableCell className="pl-4 text-sm font-medium text-gray-900">
                  {t("menus.costPerPortion")}
                </TableCell>
                <TableCell className="text-right text-sm font-medium tabular-nums text-gray-900">
                  {weightKg === null
                    ? "-"
                    : t("menus.recipe.grams", {
                        value: formatNumber(locale, weightKg * 1000, { maximumFractionDigits: 0 }),
                      })}
                </TableCell>
                <TableCell className="hidden sm:table-cell" />
                <TableCell className="pr-4 text-right text-sm font-medium tabular-nums text-gray-900">
                  {money(costPerPortion)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
      {draft && (
        <Button
          variant="ghost"
          size="sm"
          className="mb-8 text-gray-600"
          onClick={() => setAdding(true)}
        >
          <Plus />
          {t("menus.recipe.addIngredient")}
        </Button>
      )}

      <h2 className="mb-2 text-sm font-semibold text-gray-900">{t("menus.fieldMethod")}</h2>
      <div className="mb-8 rounded-lg border border-black/[0.06] bg-white px-4 py-3">
        {draft ? (
          <MethodEditor steps={draft.steps} onChange={(s) => setDraft({ ...draft, steps: s })} />
        ) : steps.length === 0 ? (
          <p className="text-sm text-gray-400">
            {canEdit ? t("menus.recipe.noMethodEdit") : t("menus.recipe.noMethod")}
          </p>
        ) : (
          <ol className="space-y-2">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-700">
                <span className="w-5 shrink-0 text-right tabular-nums text-gray-400">{i + 1}</span>
                {render(s.text, names)}
              </li>
            ))}
          </ol>
        )}
      </div>

      <h2 className="mb-2 text-sm font-semibold text-gray-900">
        {t("menus.recipes.columnOnMenus")}
      </h2>
      {onMenus.length === 0 ? (
        <p className="text-sm text-gray-500">{t("menus.recipe.onNoMenu", { site: siteName })}</p>
      ) : (
        <div className="divide-y divide-black/[0.06] overflow-hidden rounded-lg border border-black/[0.06] bg-white">
          {onMenus.map(({ menu, covers }) => {
            const open = () => {
              if (!draft) openMenu(menu.id);
            };
            return (
              <div
                key={menu.id}
                role={draft ? undefined : "button"}
                tabIndex={draft ? undefined : 0}
                onClick={open}
                onKeyDown={(e) => e.key === "Enter" && open()}
                className={`flex items-center gap-3 px-4 py-3 ${
                  draft ? "" : "cursor-pointer hover:bg-black/[0.02]"
                }`}
              >
                <LiveDot live={runsAt(menu, siteId, today)} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{label(menu)}</p>
                  <p className="truncate text-xs text-gray-400">
                    {menuLine(t, locale, menu, siteId, today)}
                  </p>
                </div>
                {draft ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={draft.covers[menu.id]}
                      inputMode="numeric"
                      aria-label={t("menus.recipe.coversLabel", { menu: label(menu) })}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          covers: { ...draft.covers, [menu.id]: e.target.value },
                        })
                      }
                      className="h-8 w-20 text-right tabular-nums"
                    />
                    <span className="text-xs text-gray-400">{t("menus.recipe.coversUnit")}</span>
                  </div>
                ) : (
                  <span className="text-sm tabular-nums text-gray-500">
                    {covers === null ? "-" : t("menus.recipe.covers", { count: covers })}
                  </span>
                )}
                {!draft && <ChevronRight className="size-4 text-gray-300" />}
              </div>
            );
          })}
        </div>
      )}

      {matching && (
        <CatalogueDialog
          title={t("menus.recipe.matchTitle", {
            name: lineName(t, matching, recipe.ingredients.indexOf(matching)),
          })}
          search={lineName(t, matching, recipe.ingredients.indexOf(matching))}
          pending={save.isPending}
          onPick={(item) => match(matching, item)}
          onClose={() => setMatching(null)}
        />
      )}
      {adding && draft && (
        <CatalogueDialog
          title={t("menus.recipe.addIngredientTitle")}
          search=""
          pending={false}
          onPick={(item) => {
            setDraft({ ...draft, added: withItem(draft.added, item) });
            setAdding(false);
          }}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  );
}

function NewRecipe({ siteId, menuId }: { siteId: string; menuId: string | null }) {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const leave = useGoBack(menuId ? `/menus/${menuId}` : "/menus/recipes");
  const menusQuery = useSiteMenus(siteId, false);
  const currency = useSiteCurrency(siteId);
  const today = useSiteToday(siteId);
  const { add } = useRecipeActions();
  const [name, setName] = useState("");
  const [lines, setLines] = useState<AddedLine[]>([]);
  const [adding, setAdding] = useState(false);
  const [course, setCourse] = useState<Course | null>(null);
  const [steps, setSteps] = useState<DraftStep[]>([]);
  // Menu id → planned covers as typed, for each menu picked.
  const [chosen, setChosen] = useState<Record<string, string>>(menuId ? { [menuId]: "" } : {});

  if (menusQuery.isPending || today === null) return <RecipeSkeleton />;
  if (menusQuery.isError) return <LoadError onRetry={() => menusQuery.refetch()} />;

  const label = (m: Menu) => menuLabel(t, locale, m);
  const menus = menusQuery.data
    .filter((m) => !isReading(m))
    .sort(
      (a, b) =>
        menuRank(a, siteId, today) - menuRank(b, siteId, today) || label(a).localeCompare(label(b)),
    );
  const picked = menus.filter((m) => m.id in chosen);
  const figures = lines.map((line) => addedFigure(locale, line));
  const money = (amount: number | null) =>
    amount === null ? "-" : formatCurrency(locale, amount, currency, 2);
  const valid =
    name.trim() !== "" &&
    picked.length > 0 &&
    figures.every((f) => f.amount !== null) &&
    Object.values(chosen).every((v) => /^\d*$/.test(v.trim()));

  function submit() {
    add.mutate(
      {
        site_id: siteId,
        name: name.trim(),
        category_slug: course ? CATEGORY_OF_COURSE[course] : null,
        method_steps: savedSteps(steps, [], []),
        lines: figures.map((f) => newLine(f.line, f.amount!, 1)),
        covers: picked.map((m) => {
          const typed = chosen[m.id].trim();
          return { menu_id: m.id, planned_covers: typed === "" ? null : Number(typed) };
        }),
      },
      {
        onSuccess: (ids) => {
          toast.success(t("menus.recipe.added", { name: name.trim() }));
          const from = picked.findIndex((m) => m.id === menuId);
          navigate(
            from >= 0 ? `/menus/recipes/${ids[from]}?from=${menuId}` : `/menus/recipes/${ids[0]}`,
            // The new recipe takes this page's place, and its back link this page's opener.
            { replace: true, state: location.state },
          );
        },
      },
    );
  }

  return (
    <>
      <RecipeBack menuId={menuId} menus={menusQuery.data} />
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="mb-3 text-lg font-medium text-gray-900">{t("menus.recipe.addTitle")}</h1>
          <label className="mb-1.5 block text-sm font-medium text-gray-700" htmlFor="recipe-name">
            {t("menus.fieldName")}
          </label>
          <Input
            id="recipe-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="max-w-sm"
          />
          <CoursePills value={course} onChange={setCourse} />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={leave}>
            {t("common.cancel")}
          </Button>
          <Button size="sm" disabled={!valid || add.isPending} onClick={submit}>
            {add.isPending ? t("common.saving") : t("menus.recipe.addTitle")}
          </Button>
        </div>
      </div>

      <h2 className="mb-2 text-sm font-semibold text-gray-900">{t("menus.ingredients")}</h2>
      {lines.length > 0 && (
        <div className="mb-2 overflow-hidden rounded-lg border border-black/[0.06] bg-white">
          <Table>
            <TableBody>
              {figures.map((figure) => (
                <AddedRow
                  key={figure.line.key}
                  figure={figure}
                  money={money}
                  onChange={(line) => setLines(lines.map((l) => (l.key === line.key ? line : l)))}
                  onRemove={() => setLines(lines.filter((l) => l.key !== figure.line.key))}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="mb-8 text-gray-600"
        onClick={() => setAdding(true)}
      >
        <Plus />
        {t("menus.recipe.addIngredient")}
      </Button>
      {adding && (
        <CatalogueDialog
          title={t("menus.recipe.addIngredientTitle")}
          search=""
          pending={false}
          onPick={(item) => {
            setLines(withItem(lines, item));
            setAdding(false);
          }}
          onClose={() => setAdding(false)}
        />
      )}

      <h2 className="mb-2 text-sm font-semibold text-gray-900">{t("menus.fieldMethod")}</h2>
      <div className="mb-8 rounded-lg border border-black/[0.06] bg-white px-4 py-3">
        <MethodEditor steps={steps} onChange={setSteps} />
      </div>

      <h2 className="text-sm font-semibold text-gray-900">{t("menus.recipes.columnOnMenus")}</h2>
      <p className="mb-2 text-xs text-gray-400">{t("menus.recipe.pickMenus")}</p>
      <div className="divide-y divide-black/[0.06] overflow-hidden rounded-lg border border-black/[0.06] bg-white">
        {menus.map((menu) => {
          const on = menu.id in chosen;
          return (
            <div key={menu.id} className="flex items-center gap-3 px-4 py-3">
              <Checkbox
                checked={on}
                aria-label={t("menus.recipe.putOn", { menu: label(menu) })}
                onCheckedChange={() =>
                  setChosen((c) => {
                    if (!(menu.id in c)) return { ...c, [menu.id]: "" };
                    return Object.fromEntries(Object.entries(c).filter(([id]) => id !== menu.id));
                  })
                }
              />
              <LiveDot live={runsAt(menu, siteId, today)} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{label(menu)}</p>
                <p className="truncate text-xs text-gray-400">
                  {menuLine(t, locale, menu, siteId, today)}
                </p>
              </div>
              {on && (
                <div className="flex items-center gap-1.5">
                  <Input
                    value={chosen[menu.id]}
                    inputMode="numeric"
                    aria-label={t("menus.recipe.coversLabel", { menu: label(menu) })}
                    onChange={(e) => setChosen({ ...chosen, [menu.id]: e.target.value })}
                    className="h-8 w-20 text-right tabular-nums"
                  />
                  <span className="text-xs text-gray-400">{t("menus.recipe.coversUnit")}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Back to the menu the page was opened from, else to All recipes. */
function RecipeBack({ menuId, menus }: { menuId: string | null; menus: Menu[] }) {
  const { t, locale } = useTranslation();
  const menu = menus.find((m) => m.id === menuId);
  if (menu) return <BackLink label={menuLabel(t, locale, menu)} to={`/menus/${menu.id}`} />;
  return <BackLink label={t("menus.recipes.title")} to="/menus/recipes" />;
}

function CoursePills({
  value,
  onChange,
}: {
  value: Course | null;
  onChange: (course: Course) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-1 flex items-center gap-1">
      {COURSES.map((c) => (
        <button
          key={c}
          type="button"
          aria-pressed={value === c}
          onClick={() => onChange(c)}
          className={`rounded-md px-2.5 py-0.5 text-sm ${
            value === c
              ? "bg-gray-100 font-medium text-gray-900"
              : "text-gray-500 hover:bg-gray-100"
          }`}
        >
          {t(COURSE_LABELS[c])}
        </button>
      ))}
    </div>
  );
}

function MethodEditor({
  steps,
  onChange,
}: {
  steps: DraftStep[];
  onChange: (steps: DraftStep[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      {steps.map((s, i) => (
        <div key={i} className="flex items-start gap-3">
          <span className="w-5 pt-2 text-right text-sm tabular-nums text-gray-400">{i + 1}</span>
          <textarea
            value={s.text}
            rows={2}
            aria-label={t("menus.recipe.stepLabel", { number: i + 1 })}
            onChange={(e) =>
              onChange(steps.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))
            }
            className="min-h-[52px] flex-1 resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-300 focus:ring-1 focus:ring-blue-500"
          />
          <button
            type="button"
            aria-label={t("menus.recipe.deleteStep", { number: i + 1 })}
            onClick={() => onChange(steps.filter((_, j) => j !== i))}
            className="mt-1.5 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="ml-8 text-gray-600"
        onClick={() => onChange([...steps, { origin: null, text: "" }])}
      >
        <Plus />
        {t("menus.recipe.addStep")}
      </Button>
    </div>
  );
}

/** Pick a catalogue item: to match a line, or to add one. */
function CatalogueDialog({
  title,
  search: initial,
  pending,
  onPick,
  onClose,
}: {
  title: string;
  search: string;
  pending: boolean;
  onPick: (item: InventoryItem) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState(initial);
  const items = useInventorySearch(search.trim());
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-300" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t("menus.recipe.matchSearch")}
            className="h-9 pl-9"
          />
        </div>
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {items.isPending ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)
          ) : items.isError ? (
            <LoadError onRetry={() => items.refetch()} />
          ) : items.data.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-gray-500">{t("menus.noItemsFound")}</p>
          ) : (
            items.data.map((item) => (
              <button
                key={item.id}
                type="button"
                disabled={pending}
                className="w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => onPick(item)}
              >
                {itemDisplayName(item)}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A line added on the page, as a row of the ingredients table: amount per portion, unit, cost. */
function AddedRow({
  figure,
  money,
  onChange,
  onRemove,
}: {
  figure: AddedFigure;
  money: (amount: number | null) => string;
  onChange: (line: AddedLine) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { line } = figure;
  const name = itemDisplayName(line.item);
  return (
    <TableRow>
      <TableCell className="whitespace-normal py-2.5 pl-4">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">{name}</p>
            <p className="text-xs text-gray-400">{t("menus.detail.matched")}</p>
          </div>
          <RemoveButton name={name} onClick={onRemove} />
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Input
            value={line.amount}
            inputMode="decimal"
            aria-label={t("menus.recipe.amountLabel", { name })}
            aria-invalid={figure.amount === null}
            onChange={(e) => onChange({ ...line, amount: e.target.value })}
            className="h-8 w-16 text-right tabular-nums"
          />
          <div role="group" aria-label={t("menus.recipe.unitOf", { name })} className="flex">
            {SHOWN_UNITS.map((unit) => (
              <button
                key={unit}
                type="button"
                aria-pressed={line.unit === unit}
                onClick={() => onChange({ ...line, unit })}
                className={`rounded px-1.5 py-0.5 text-xs ${
                  line.unit === unit
                    ? "bg-gray-100 font-medium text-gray-900"
                    : "text-gray-400 hover:bg-gray-100"
                }`}
              >
                {t(UNIT_KEYS[unit])}
              </button>
            ))}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden text-right text-sm tabular-nums text-gray-500 sm:table-cell">
        {money(line.item.price_per_kg)}
      </TableCell>
      <TableCell className="pr-4 text-right text-sm tabular-nums text-gray-900">
        {money(figure.cost)}
      </TableCell>
    </TableRow>
  );
}

function RemoveButton({ name, onClick }: { name: string; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={t("menus.recipe.removeLine", { name })}
      onClick={onClick}
      className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
    >
      <Trash2 className="size-3.5" />
    </button>
  );
}

function LiveDot({ live }: { live: boolean }) {
  return (
    <span className={`size-1.5 shrink-0 rounded-full ${live ? "bg-emerald-500" : "bg-gray-300"}`} />
  );
}

function RecipeSkeleton() {
  return (
    <>
      <div className="mb-5 space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-6 w-64 max-w-full" />
        <Skeleton className="h-4 w-40" />
      </div>
      <RowsSkeleton />
    </>
  );
}

/** "Live · Cycle · 10 – 23 Aug · 11:30–14:30": how a menu stands at the site. */
function menuLine(t: TFunction, locale: Locale, menu: Menu, siteId: string, today: string) {
  const state = runsAt(menu, siteId, today)
    ? t("menus.detail.live")
    : hasEnded(menu, today)
      ? t("menus.groups.ended")
      : t("menus.detail.off");
  return [state, t(CADENCE_LABELS[menu.cadence]), datesLabel(locale, menu), serviceTime(menu)]
    .filter(Boolean)
    .join(" · ");
}

function lineName(t: TFunction, line: RecipeLine, index: number): string {
  return (
    line.item_name ?? line.description ?? t("menus.ingredientPlaceholder", { number: index + 1 })
  );
}

/** A line's amount per portion, in the unit it is shown in (g, ml or each). */
function perPortion(line: RecipeLine, portions: number): number {
  return (line.quantity * SHOWN_PER_STORED[line.unit_of_measure]) / portions;
}

function amountInput(locale: Locale, amount: number): string {
  return formatNumber(locale, amount, { maximumFractionDigits: 1, useGrouping: false });
}

/** Step text with each `{n}` replaced by line n's name. */
function render(text: string, names: string[]): string {
  return text.replace(REF, (ref, n: string) => names[Number(n)] ?? ref);
}

/** The steps as stored, or a legacy plain-text method read as one step a line. */
function storedSteps(recipe: Recipe): MethodStep[] {
  if (recipe.method_steps) return recipe.method_steps;
  return (recipe.method ?? "")
    .split("\n")
    .map((line) => line.replace(/^\s*\d+[.)]\s*/, "").trim())
    .filter(Boolean)
    .map((text, i) => ({ step: i + 1, text, ingredient_refs: [] }));
}

/**
 * The steps to save. An unchanged step keeps its stored text and refs, renumbered to the lines that
 * stay (`kept`, the stored indices in order): a ref to a removed line becomes that line's name. An
 * edited step is plain text.
 */
function savedSteps(steps: DraftStep[], names: string[], kept: number[]): MethodStep[] {
  const at = new Map(kept.map((stored, i) => [stored, i]));
  return steps
    .filter((s) => s.text.trim() !== "")
    .map((s, i) => {
      if (!s.origin || s.text !== render(s.origin.text, names)) {
        return { step: i + 1, text: s.text.trim(), ingredient_refs: [] };
      }
      const text = s.origin.text.replace(REF, (ref, n: string) => {
        const moved = at.get(Number(n));
        return moved === undefined ? (names[Number(n)] ?? ref) : `{${moved}}`;
      });
      const refs = s.origin.ingredient_refs.flatMap((n) => {
        const moved = at.get(n);
        return moved === undefined ? [] : [moved];
      });
      return { ...s.origin, step: i + 1, text, ingredient_refs: refs };
    });
}

/**
 * An added line's figures per portion. Weighed in g or ml (ml read as g, as for the other lines);
 * an "each" has no weight or cost until saved, when its pack size is read.
 */
function addedFigure(locale: Locale, line: AddedLine): AddedFigure {
  const typed = parseLocaleNumber(locale, line.amount);
  const amount = typed !== null && typed > 0 ? typed : null;
  if (amount === null || line.unit === "each") return { line, amount, cost: null, weightKg: null };
  const kg = amount / 1000;
  const price = line.item.price_per_kg;
  return { line, amount, cost: price === null ? null : kg * price, weightKg: kg };
}

/** An added line as saved: its quantity for the whole yield, in the unit it is stored in. */
function newLine(line: AddedLine, amount: number, portions: number): NewLine {
  const unit = STORED_AS[line.unit];
  return {
    inventory_item_id: line.item.id,
    quantity: (amount * portions) / SHOWN_PER_STORED[unit],
    unit_of_measure: unit,
  };
}

function withItem(lines: AddedLine[], item: InventoryItem): AddedLine[] {
  const key = Math.max(0, ...lines.map((l) => l.key)) + 1;
  return [...lines, { key, item, amount: "", unit: "g" }];
}
